import { captureFrontendError } from 'utils/captureFrontendError'
import { isQuotaExceededError } from 'utils/isQuotaExceededError'
import { ionicStore } from '../../ionicStorage'
import { getStorageEstimate } from './getStorageEstimate'
import { purgeMapCaches } from './purgeMapCaches'

/**
 * Writes to the store and never rejects. When the origin is out of storage, the service worker's map
 * caches are cleared and the write is retried once: on the affected devices they hold the space, since
 * Chromium counts every cached map tile as several MB. That only happens online, so an offline map keeps
 * its imagery. Every outcome except a plain success is reported, with the storage estimate attached to
 * quota errors.
 */
export const setWithQuotaRecovery = async (
  key: string,
  value: unknown,
  tags: Record<string, string>,
): Promise<void> => {
  try {
    await ionicStore.set(key, value)
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      captureFrontendError(error, { tags })

      return
    }

    const estimateBefore = await getStorageEstimate()

    if (!navigator.onLine) {
      captureFrontendError(error, {
        tags: { ...tags, quotaRecovery: 'skipped' },
        extra: { storageEstimate: { before: estimateBefore } },
      })

      return
    }

    const purged = await purgeMapCaches()

    try {
      await ionicStore.set(key, value)

      captureFrontendError('Recovered from QuotaExceededError by clearing the map caches', {
        level: 'warning',
        tags: { ...tags, quotaRecovery: 'recovered' },
        extra: { storageEstimate: { before: estimateBefore, after: await getStorageEstimate() }, purged },
      })
    } catch (errorAfterPurge) {
      captureFrontendError(errorAfterPurge, {
        tags: { ...tags, quotaRecovery: 'failed' },
        extra: { storageEstimate: { before: estimateBefore, after: await getStorageEstimate() }, purged },
      })
    }
  }
}
